// src/pages/qc/PageCustomerComplaints.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { downloadPdf } from "../../apiService";
import { CcCreateModal } from "./CcCreateModal";
import { CcDetailModal } from "./CcDetailModal";
import { fmtDate, currentMonthKey } from "../../utils/dateUtils";
import { MonthYearSelect } from "../../components/MonthYearSelect";
import { useToast } from "../../components/Toast";

const STATUS_STYLE = {
  OPEN:      { bg: "#fef3c7", color: "#92400e" },
  RESPONDED: { bg: "#dbeafe", color: "#1e40af" },
  CLOSED:    { bg: "#d1fae5", color: "#065f46" },
};
const STATUS_LABEL = { OPEN: "Open", RESPONDED: "Responded", CLOSED: "Closed" };

const TYPE_LABELS = { WB: "Water Based", BF: "Brake Fluid", COOLANT: "Coolant", HM: "Hot Melt" };
const TYPE_COLORS = {
  WB:      { bg: "#dbeafe", color: "#1e40af" },
  BF:      { bg: "#fee2e2", color: "#991b1b" },
  COOLANT: { bg: "#d1fae5", color: "#065f46" },
  HM:      { bg: "#fef3c7", color: "#92400e" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

function TypeBadge({ type }) {
  const c = TYPE_COLORS[type] ?? { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{ background: c.bg, color: c.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {TYPE_LABELS[type] ?? type}
    </span>
  );
}


export function PageCustomerComplaints({ myPerms, fgs }) {
  const showToast = useToast();
  const [rows,     setRows]    = useState([]);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState(null);
  const [search,   setSearch]   = useState("");
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter,   setTypeFilter]   = useState("ALL");
  const [createModal,  setCreateModal]  = useState(false);
  const [detailRow,    setDetailRow]    = useState(null);

  const canEdit   = myPerms?.includes("QC_EDIT") || myPerms?.includes("CRM_VIEW");
  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    api.fetchComplaints()
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      const matchMonth  = (r.createdAt || "").startsWith(monthKey);
      const matchStatus = statusFilter === "ALL" || r.status === statusFilter;
      const matchType   = typeFilter   === "ALL" || r.productType === typeFilter;
      const matchQ      = !q ||
        (r.ccNumber      || "").toLowerCase().includes(q) ||
        (r.customerName  || "").toLowerCase().includes(q) ||
        (r.productName   || "").toLowerCase().includes(q) ||
        (r.productId     || "").toLowerCase().includes(q) ||
        (r.reportedBy    || "").toLowerCase().includes(q);
      return matchMonth && matchStatus && matchType && matchQ;
    });
  }, [rows, search, statusFilter, typeFilter, monthKey]);

  async function handleCreate(body) {
    const created = await api.createComplaint(body);
    setRows(old => [created, ...old]);
    showToast("Complaint logged");
    setCreateModal(false);
  }

  async function handleRespond(body) {
    const updated = await api.respondToComplaint(detailRow.id, body);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("Response saved");
  }

  async function handleClose() {
    const updated = await api.closeComplaint(detailRow.id);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("Complaint closed");
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this complaint? This cannot be undone.")) return;
    await api.deleteComplaint(id);
    setRows(old => old.filter(r => r.id !== id));
    showToast("Complaint deleted", "info");
    if (detailRow?.id === id) setDetailRow(null);
  }

  function handleDownloadPdf(row) {
    downloadPdf(`/api/qc/cc/${row.id}/pdf`, row.ccNumber + ".pdf");
  }

  const colCount = 8 + (canDelete ? 1 : 0);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Customer Complaints</div>
          <div className="page-subtitle">S&M-FT-14 · Log, investigate and close customer complaints</div>
        </div>
        {canEdit && (
          <div className="page-header-right">
            <button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ New Complaint</button>
          </div>
        )}
      </div>

      <div className="action-bar" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
        <input className="form-input" placeholder="Search CC no, customer, product…"
          value={search} onChange={e => setSearch(e.target.value)} style={{ width: 260 }} />
        <select className="form-select" value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)} style={{ width: 160 }}>
          <option value="ALL">All Statuses</option>
          <option value="OPEN">Open</option>
          <option value="RESPONDED">Responded</option>
          <option value="CLOSED">Closed</option>
        </select>
        <select className="form-select" value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)} style={{ width: 150 }}>
          <option value="ALL">All Types</option>
          <option value="WB">Water Based</option>
          <option value="BF">Brake Fluid</option>
          <option value="COOLANT">Coolant</option>
          <option value="HM">Hot Melt</option>
        </select>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>CC Number</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Product</th>
                <th>Date</th>
                <th>Reported By</th>
                <th>Status</th>
                <th>PDF</th>
                {canDelete && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => (
                <tr key={row.id} style={{ cursor: "pointer" }}
                  onClick={() => setDetailRow(row)}>
                  <td className="fw-600" style={{ fontFamily: "monospace" }}>{row.ccNumber}</td>
                  <td>{row.customerName}</td>
                  <td><TypeBadge type={row.productType} /></td>
                  <td style={{ fontSize: 12 }}>{row.productName || row.productId}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDate(row.complaintDate)}</td>
                  <td>{row.reportedBy}</td>
                  <td><StatusBadge status={row.status} /></td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="btn-small" onClick={() => handleDownloadPdf(row)}
                      title="Download PDF" style={{ padding: "3px 10px" }}>
                      ⬇ PDF
                    </button>
                  </td>
                  {canDelete && (
                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn-small btn-danger" onClick={() => handleDelete(row.id)}>Del</button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                    No complaints found. {canEdit && "Click '+ New Complaint' to log one."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {createModal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setCreateModal(false); }}>
          <CcCreateModal fgs={fgs} onSave={handleCreate} onCancel={() => setCreateModal(false)} />
        </div>
      )}

      {detailRow && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setDetailRow(null); }}>
          <CcDetailModal
            cc={detailRow}
            canEdit={canEdit}
            onRespond={handleRespond}
            onClose={handleClose}
            onCancel={() => setDetailRow(null)}
          />
        </div>
      )}
    </>
  );
}
